import { useCallback, useEffect, useRef } from 'react';

export const useConversationScroll = (containerRef, contentRef) => {
  const follow = useRef(true);
  const followLatest = useCallback(() => {
    follow.current = true;
    const container = containerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;
    let frame;
    let manualUntil = 0;
    const markManual = () => { manualUntil = Date.now() + 1200; };
    const onWheel = (event) => {
      markManual();
      if (event.deltaY < 0) follow.current = false;
    };
    const onKeyDown = (event) => {
      if (['ArrowUp', 'PageUp', 'Home', 'ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) markManual();
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) follow.current = false;
    };
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (follow.current) container.scrollTop = container.scrollHeight;
      });
    };
    const onScroll = () => {
      // Content growth also emits scroll events; only user gestures may disable following.
      if (Date.now() <= manualUntil) {
        follow.current = container.scrollHeight - container.scrollTop - container.clientHeight < 40;
      }
    };
    container.addEventListener('scroll', onScroll);
    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('touchstart', markManual, { passive: true });
    container.addEventListener('touchmove', markManual, { passive: true });
    container.addEventListener('pointerdown', markManual);
    container.addEventListener('keydown', onKeyDown);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(content);
    observer?.observe(container);
    const mutations = new MutationObserver(update);
    mutations.observe(content, { childList: true, subtree: true, characterData: true });
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      mutations.disconnect();
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', markManual);
      container.removeEventListener('touchmove', markManual);
      container.removeEventListener('pointerdown', markManual);
      container.removeEventListener('keydown', onKeyDown);
    };
  }, [containerRef, contentRef]);
  return followLatest;
};
